import React, { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Upload, FileText, Check, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { key: "consignors", label: "Consignors", hint: "Name, Email, Phone, Payout %" },
  { key: "products", label: "Products", hint: "Product Name, SKU, Original Price, Consignor, Payout %" },
  { key: "sales", label: "Sales", hint: "Product Name, SKU, Sale Date, Sale Price, Discount, Consignor, Payout %" },
];

const FIELD_LABELS = {
  name: "Name", email: "Email", phone: "Phone", payout_pct: "Payout %",
  product_name: "Product Name", sku: "SKU", original_price: "Original Price",
  consignor: "Consignor", sale_date: "Sale Date", sale_price: "Sale Price", discount: "Discount",
};

export default function Import() {
  const [kind, setKind] = useState("sales");
  const [step, setStep] = useState(1);
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  const reset = () => { setStep(1); setContent(""); setPreview(null); setMapping({}); setResult(null); };

  const onFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const text = await file.text(); setContent(text);
    try {
      const { data } = await api.post(`/import/${kind}/preview`, { content: text });
      setPreview(data);
      const auto = {};
      data.headers.forEach((h) => {
        const norm = h.toLowerCase().replace(/[^a-z]/g, "");
        const match = data.target_fields.find((f) => norm.includes(f.replace(/_/g, "").replace("pct", "")) || FIELD_LABELS[f]?.toLowerCase().replace(/[^a-z]/g, "") === norm);
        if (match) auto[h] = match;
      });
      setMapping(auto); setStep(2);
    } catch (e) { toast.error("Could not read CSV"); }
  };

  const commit = async () => {
    try {
      const { data } = await api.post(`/import/${kind}/commit`, { content, mapping });
      setResult(data); setStep(4);
      toast.success(`Imported ${data.imported} records`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };

  const mappedFields = Object.values(mapping);
  const requiredMet = kind === "consignors" ? mappedFields.includes("name")
    : kind === "products" ? mappedFields.includes("product_name")
    : mappedFields.includes("product_name") && mappedFields.includes("sale_price");

  const STEPS = ["Upload File", "Map Columns", "Preview & Validate", "Confirm & Import"];

  return (
    <div>
      <PageHeader title="CSV Import" subtitle="Test the app instantly — no Shopify or WooCommerce needed" />

      <div className="flex flex-wrap gap-2 mb-6">
        {KINDS.map((k) => (
          <button key={k.key} data-testid={`import-kind-${k.key}`} onClick={() => { setKind(k.key); reset(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${kind === k.key ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            Import {k.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 text-sm ${step > i ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step > i + 1 ? "bg-emerald-700 text-white" : step === i + 1 ? "bg-emerald-100 text-emerald-800 border border-emerald-600" : "bg-slate-100"}`}>{step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}</span>
              <span className="hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="h-px w-6 bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        {step === 1 && (
          <div className="text-center py-10">
            <div className="p-4 rounded-2xl bg-emerald-50 inline-block mb-4"><Upload className="w-8 h-8 text-emerald-700" /></div>
            <h3 className="font-display font-bold text-lg text-slate-900">Upload {KINDS.find((k) => k.key === kind).label} CSV</h3>
            <p className="text-slate-500 text-sm mt-1 mb-2">Expected columns: {KINDS.find((k) => k.key === kind).hint}</p>
            <label className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-5 py-2.5 rounded-lg text-sm cursor-pointer mt-3">
              <FileText className="w-4 h-4" /> Choose CSV File
              <input data-testid="csv-file-input" type="file" accept=".csv" className="hidden" onChange={onFile} />
            </label>
          </div>
        )}

        {step === 2 && preview && (
          <div>
            <h3 className="font-display font-bold text-slate-900 mb-1">Map your columns</h3>
            <p className="text-sm text-slate-500 mb-4">{preview.total_rows} rows detected. Match CSV columns to fields.</p>
            <div className="space-y-2">
              {preview.headers.map((h) => (
                <div key={h} className="flex items-center gap-3" data-testid={`map-row-${h}`}>
                  <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2 text-sm font-mono-fin text-slate-700 truncate">{h}</div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <select data-testid={`map-select-${h}`} value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
                    <option value="">— Skip —</option>
                    {preview.target_fields.map((f) => <option key={f} value={f}>{FIELD_LABELS[f]}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {!requiredMet && <p className="text-sm text-amber-700 flex items-center gap-1 mt-3"><AlertTriangle className="w-4 h-4" /> Map the required field(s) to continue.</p>}
            <div className="flex justify-between mt-6">
              <button onClick={reset} className="text-sm text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-lg">Start over</button>
              <button data-testid="to-preview-btn" disabled={!requiredMet} onClick={() => setStep(3)} className="bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-lg text-sm">Preview Data</button>
            </div>
          </div>
        )}

        {step === 3 && preview && (
          <div>
            <h3 className="font-display font-bold text-slate-900 mb-4">Preview (first 5 rows)</h3>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm" data-testid="import-preview-table">
                <thead className="bg-slate-50"><tr className="text-left text-xs font-semibold uppercase text-slate-500">
                  {preview.headers.map((h) => <th key={h} className="px-3 py-2">{mapping[h] ? FIELD_LABELS[mapping[h]] : <span className="text-slate-300">{h} (skip)</span>}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.sample.map((row, i) => (
                    <tr key={i}>{row.map((c, j) => <td key={j} className="px-3 py-2 text-slate-700">{c}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between mt-6">
              <button onClick={() => setStep(2)} className="text-sm text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-lg">Back</button>
              <button data-testid="confirm-import-btn" onClick={commit} className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-5 py-2 rounded-lg text-sm">Import {preview.total_rows} Rows</button>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="text-center py-8">
            <div className="p-4 rounded-2xl bg-emerald-50 inline-block mb-4"><Check className="w-8 h-8 text-emerald-700" /></div>
            <h3 className="font-display font-bold text-lg text-slate-900">Import Complete</h3>
            <p className="text-slate-600 mt-1"><b className="text-emerald-700">{result.imported}</b> records imported{result.errors.length > 0 && <>, <b className="text-rose-600">{result.errors.length}</b> skipped</>}.</p>
            {result.errors.length > 0 && (
              <div className="text-left max-w-md mx-auto mt-4 bg-rose-50 border border-rose-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                {result.errors.slice(0, 20).map((e, i) => <p key={i} className="text-xs text-rose-700">Row {e.row}: {e.error}</p>)}
              </div>
            )}
            <button data-testid="import-again-btn" onClick={reset} className="mt-5 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-5 py-2 rounded-lg text-sm">Import More</button>
          </div>
        )}
      </div>
    </div>
  );
}
